import {Locale} from '@/utils/constants'
import {getPathLocales, getWithoutLocale} from '@/utils/i18n/restrictedPaths'
import {getPreferredPathLocale, isPreferenceRoutedPath} from '@/utils/i18n/routing'

/*
 * Reading the current URL from the browser, for the places that deliberately do not subscribe to
 * it through `useSearchParams()` — see docs/static-rendering.md.
 *
 * All of these touch `window`, so they may only be called from an effect or an event handler.
 * Calling one while rendering crashes on the server, and guarding that with
 * `typeof window !== 'undefined'` produces a hydration mismatch instead — which is worse, because
 * it is silent.
 */

/** The current path with its query string, locale prefix stripped. */
export const getCurrentPath = () =>
  `${getWithoutLocale(window.location.pathname)}${window.location.search}`

/**
 * This page's own version in another language, or null when it has none: its hreflang alternate -
 * an academy or blog article has a slug per language, so the same path would be a 404 - or the same
 * path on the homepage and the portal, where that language serves it.
 */
export const getCurrentPathVersionIn = (locale: Locale) => {
  const alternate = document.querySelector<HTMLLinkElement>(
    `link[rel="alternate"][hreflang="${locale}"]`,
  )
  if (alternate) {
    return `${getWithoutLocale(new URL(alternate.href).pathname)}${window.location.search}`
  }
  return getPreferredPathLocale(getWithoutLocale(window.location.pathname), locale)
    ? getCurrentPath()
    : null
}

/**
 * The closest page to this one in another language, for a language switch - see "Switching
 * language" in docs/visitor-preferences.md.
 */
export const getCurrentPathIn = (locale: Locale) => {
  const version = getCurrentPathVersionIn(locale)
  if (version) {
    return version
  }

  const pathname = getWithoutLocale(window.location.pathname)
  // The same path in every language - in English where that language has none
  if (isPreferenceRoutedPath(pathname)) {
    return getCurrentPath()
  }

  // The page and its whole section exist only in other languages
  if (!getPathLocales(pathname).includes(locale)) {
    return '/'
  }

  const hasAlternates = document.querySelector('link[rel="alternate"][hreflang]') !== null
  return hasAlternates ? `/${pathname.split('/')[1]}` : getCurrentPath()
}

/** The current query string, for reading or editing individual params. */
export const getCurrentSearchParams = () => new URLSearchParams(window.location.search)

/**
 * Removes params from the address without navigating: an explicit choice replaces what a link set,
 * so a reload, or a language switch (which keeps the query string), must not bring it back.
 */
export const removeCurrentParams = (names: string[]) => {
  const url = new URL(window.location.href)
  if (!names.some((name) => url.searchParams.has(name))) {
    return
  }
  names.forEach((name) => url.searchParams.delete(name))
  window.history.replaceState(null, '', url)
}

/** The current query string as a plain record. */
export const getCurrentParams = (): Record<string, string> =>
  Object.fromEntries(getCurrentSearchParams())
